import { useCallback, useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../config/authConfig';

export interface AuthUser {
  name?: string;
  email?: string;
  username?: string;
}

// Token acquisition, the display-name lookup and logOut all live in
// utils/auth.ts, which talks to the msalInstance singleton directly rather than
// to a module-level ref this hook has to populate. They are re-exported here so
// the existing `@/hooks` barrel imports keep working.
export {
  getMslToken,
  getApiToken,
  getToken,
  getCurrentUserDisplayName,
  logOut,
} from '../utils/auth';
export type { TokenAudience } from '../utils/auth';

/**
 * Custom hook to manage authentication state and operations
 */
export const useAuth = () => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // Initialize synchronously from MSAL accounts to avoid a null→populated
  // flicker when the component remounts (e.g. navigating between stepper steps).
  const [user, setUser] = useState<AuthUser | null>(() => {
    if (isAuthenticated && accounts.length > 0) {
      const account = accounts[0];
      return { name: account.name, email: account.username, username: account.username };
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(() => inProgress !== InteractionStatus.None);

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      const account = accounts[0];
      setUser({
        name: account.name,
        email: account.username,
        username: account.username,
      });
    } else {
      setUser(null);
    }
    setIsLoading(inProgress !== InteractionStatus.None);
  }, [isAuthenticated, accounts, inProgress]);

  const login = useCallback(async () => {
    try {
      setIsLoading(true);
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [instance]);

  const loginRedirect = useCallback(async () => {
    try {
      setIsLoading(true);
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login redirect error:', error);
      throw error;
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
        mainWindowRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [instance]);

  const logoutRedirect = useCallback(async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout redirect error:', error);
      throw error;
    }
  }, [instance]);

  return {
    isAuthenticated,
    isLoading,
    user,
    login,
    loginRedirect,
    logout,
    logoutRedirect,
    accounts,
  };
};
